import { Skeleton } from "../ui/skeleton";

export const PortfolioDashboardSkeleton = () => {
  return (
    <div
      className="w-full min-h-screen bg-background"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading portfolio dashboard"
    >
      {/* Top Live Strip */}
      <div className="mb-6 w-full p-2.5 rounded-xl bg-card/50 border border-border/30 flex items-center justify-between gap-4">
        <Skeleton className="h-6 w-32 rounded-lg" />
        <Skeleton className="h-6 flex-1 rounded-lg" />
      </div>

      {/* Header with Quick Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
        <div className="space-y-2">
          <Skeleton className="h-8 w-56 rounded-xl" />
          <Skeleton className="h-4 w-72 rounded-lg" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-10 w-28 rounded-xl" />
          <Skeleton className="h-10 w-28 rounded-xl" />
          <Skeleton className="h-10 w-28 rounded-xl" />
        </div>
      </div>

      {/* 4 Metric Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="p-6 rounded-2xl bg-gradient-to-br from-card to-card/50 border border-border/50 space-y-3"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="w-9 h-9 rounded-xl" />
            </div>
            <Skeleton className="h-8 w-36" />
            <div className="flex items-center gap-2 pt-1">
              <Skeleton className="h-4 w-16 rounded-full" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>

      {/* Chart Section + Quick Actions */}
      <div className="grid lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-8 p-6 rounded-2xl bg-card/50 border border-border/50 space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-8 w-48 rounded-lg" />
          </div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
        <div className="lg:col-span-4 p-6 rounded-2xl bg-card/50 border border-border/50 space-y-3">
          <Skeleton className="h-5 w-32 mb-4" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </div>

      {/* Holdings Section */}
      <div className="bg-gradient-to-br from-card to-card/50 border border-border/50 rounded-2xl p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-2">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-28" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-32 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-border/60 mb-2">
          <div className="col-span-6 lg:col-span-7">
            <Skeleton className="h-3 w-16" />
          </div>
          <div className="col-span-3 lg:col-span-2 text-right">
            <Skeleton className="h-3 w-16 ml-auto" />
          </div>
          <div className="col-span-3 text-right">
            <Skeleton className="h-3 w-16 ml-auto" />
          </div>
        </div>

        {/* Rows */}
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-4 items-center p-4 rounded-xl bg-background/50 border border-border/30"
            >
              <div className="col-span-6 lg:col-span-7 flex items-center gap-4 min-w-0">
                <Skeleton className="w-12 h-12 rounded-xl flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-44" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <div className="col-span-3 lg:col-span-2 text-right space-y-1">
                <Skeleton className="h-4 w-16 ml-auto" />
                <Skeleton className="h-3 w-12 ml-auto" />
              </div>
              <div className="col-span-3 text-right">
                <Skeleton className="h-5 w-24 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Grid: Rental Earnings & Transactions */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="p-6 rounded-2xl bg-card/50 border border-border/50 space-y-4">
          <Skeleton className="h-5 w-36 mb-2" />
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-28 w-full rounded-xl" />
        </div>
        <div className="p-6 rounded-2xl bg-card/50 border border-border/50 space-y-3">
          <Skeleton className="h-5 w-44 mb-2" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
};
